import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Settings2, X } from "lucide-react";
import { Container } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { goals, roles, type PersonalizationMode } from "@/content/personalization";
import { usePersonalization } from "@/lib/personalization";
import { trackEvent } from "@/lib/telemetry";

/**
 * Visible, optional personalization prompt.
 *
 * Design constraints:
 *  - Always clearly labeled as optional, with what it does and where the
 *    selection is stored. Nothing is hidden or inferred.
 *  - Skipping keeps the complete default experience.
 *  - A selection can be changed or cleared at any time from the same surface.
 *  - Rendered as a semantic radio group; every option is keyboard reachable.
 */
export const PersonalizationPrompt = () => {
  const { ready, selection, profile, dismissed, select, clear, dismiss } = usePersonalization();
  const [mode, setMode] = useState<PersonalizationMode>("goal");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (ready && !selection && !dismissed) trackEvent("personalization_prompt_shown", "home");
  }, [ready, selection, dismissed]);

  if (!ready) return null;

  /* Selected state: a compact, reversible summary bar. */
  if (selection && profile && !editing) {
    return (
      <div className="border-b border-border bg-surface">
        <Container>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-4">
            <p className="ds-caption text-foreground">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-primary">
                {selection.mode === "goal" ? "Focus" : "Role"}
              </span>{" "}
              <span className="font-semibold">{profile.label}</span> — {profile.confirmation}
            </p>
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setMode(selection.mode);
                  setEditing(true);
                }}
              >
                <Settings2 className="mr-1.5 size-4" aria-hidden />
                Change
              </Button>
              <Button variant="ghost" size="sm" onClick={clear}>
                <RotateCcw className="mr-1.5 size-4" aria-hidden />
                Clear
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (dismissed && !editing) return null;

  const options = mode === "goal" ? goals : roles;
  const legend =
    mode === "goal" ? "What would you like to improve?" : "What is your role?";

  return (
    <div className="border-b border-border bg-surface">
      <Container>
        <div className="py-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-primary">
                Optional
              </p>
              <h2 className="ds-h5 mt-1 text-foreground">{legend}</h2>
              <p className="ds-caption mt-1 max-w-2xl text-muted-foreground">
                We will reorder the sections below, suggest relevant capabilities and resources, and
                adjust the wording of the demo request. Every page stays available from the
                navigation, and nothing is hidden.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="flex rounded-full border border-border p-0.5"
                role="tablist"
                aria-label="Choose how to personalize"
              >
                {(["goal", "role"] as PersonalizationMode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={mode === m}
                    onClick={() => setMode(m)}
                    className={cn(
                      "min-h-11 rounded-full px-4 text-xs font-semibold transition-colors sm:min-h-0 sm:py-1",
                      mode === m
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}

                  >
                    {m === "goal" ? "By goal" : "By role"}
                  </button>
                ))}
              </div>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Dismiss personalization prompt"
                onClick={() => {
                  setEditing(false);
                  dismiss();
                }}
              >
                <X className="size-4" aria-hidden />
              </Button>
            </div>
          </div>

          <fieldset className="mt-5">
            <legend className="sr-only">{legend}</legend>
            <div className="flex flex-wrap gap-2">
              {options.map((option) => {
                const isActive = selection?.mode === mode && selection.id === option.id;
                return (
                  <label
                    key={option.id}
                    className={cn(
                      "cursor-pointer rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                      "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                      isActive
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-foreground hover:border-primary/50",
                    )}
                  >
                    <input
                      type="radio"
                      name={`personalization-${mode}`}
                      value={option.id}
                      checked={isActive}
                      onChange={() => {
                        select(mode, option.id);
                        setEditing(false);
                      }}
                      className="sr-only"
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>
          </fieldset>

          <p className="ds-legal mt-4 text-muted-foreground">
            Your choice is a professional interest only. It is stored in this browser, is never sent
            to us with your identity, never creates an account, and can be cleared at any time. We do
            not infer a clinical role from any other information. Regulatory notices, claims, and
            navigation are identical in every version.
          </p>
          {/* Separate row so both controls clear the 44px touch target on phones. */}
          <div className="mt-2 flex flex-wrap items-center gap-x-4">
            <button
              type="button"
              onClick={() => { setEditing(false); dismiss(); }}
              className="ds-legal inline-flex min-h-11 items-center text-muted-foreground underline sm:min-h-0"
            >
              Continue without personalizing
            </button>
            <Link
              to="/privacy"
              className="ds-legal inline-flex min-h-11 items-center text-muted-foreground underline sm:min-h-0"
            >
              Privacy
            </Link>
          </div>

        </div>
      </Container>
    </div>
  );
};
